import { logoFor } from "./content";
import {
  ASSISTANT_CHIPS,
  ASSISTANT_REPLIES,
  PLATFORM_BARS,
  PLATFORM_SIDEBAR,
  PLATFORM_STATS,
  PLATFORM_TASKS,
  PLATFORM_WEEKS,
} from "./platformData";

/**
 * The dark dashboard rendering inside the Platform screen.
 *
 * Extracted from Platform.tsx only because of its size — it is one element in
 * the canvas, and nothing else renders it. The `[data-chart]` / `[data-bar]`
 * hooks here are the only ones on the page: the motion runtime grows these four
 * bars from `scaleY(.05)` on a 110ms cascade when the chart scrolls into view.
 */

const dot: React.CSSProperties = {
  width: 10,
  height: 10,
  borderRadius: "50%",
  background: "rgba(255,255,255,.16)",
};

const sidebarItem: React.CSSProperties = {
  padding: "8px 10px",
  fontSize: 12.5,
  color: "rgba(242,244,246,.6)",
};

const card: React.CSSProperties = {
  background: "#1B1F25",
  border: "1px solid rgba(255,255,255,.08)",
  borderRadius: 12,
  padding: "12px 14px",
};

const panel: React.CSSProperties = {
  background: "#1B1F25",
  border: "1px solid rgba(255,255,255,.08)",
  borderRadius: 12,
  padding: "14px 16px",
};

const weekLabel: React.CSSProperties = {
  flex: 1,
  textAlign: "center",
  fontSize: 10,
  color: "rgba(242,244,246,.55)",
  fontFamily: "'Manrope', sans-serif",
};

const reply: React.CSSProperties = {
  alignSelf: "flex-start",
  maxWidth: "92%",
  background: "rgba(255,255,255,.06)",
  borderRadius: "10px 10px 10px 3px",
  padding: "8px 11px",
  fontSize: 11.5,
  color: "rgba(242,244,246,.85)",
};

export default function DashboardDark() {
  return (
    <div
      style={{
        background: "#15181D",
        border: "1px solid rgba(255,255,255,.12)",
        borderRadius: 16,
        boxShadow: "0 40px 90px rgba(0,0,0,.5)",
        overflow: "hidden",
        /* Its own ink, and a literal rather than a token, for the same reason
           the mark picks its master from the GROUND and not the page theme:
           this panel is #15181D in both themes, so its text is light in both.
           Every muted value below is already an explicit rgba(242,244,246,...);
           the PRIMARY text -- headings, the figures, the assistant replies --
           had no colour of its own and inherited the section's. That was
           invisible while the section was hard-dark too, and turned into
           near-black text on a dark panel the moment the band learned to
           follow the theme. */
        color: "#F2F4F6",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 18px",
          borderBottom: "1px solid rgba(255,255,255,.09)",
        }}
      >
        <span style={dot} />
        <span style={dot} />
        <span style={dot} />
        <span
          style={{
            marginLeft: 8,
            fontFamily: "'Manrope', sans-serif",
            fontSize: 11.5,
            color: "rgba(242,244,246,.55)",
          }}
        >
          AI2Aim Dashboard
        </span>
      </div>
      {/* Sidebar beside the content from tablet up; on a phone the sidebar
          goes and the grids below stack (global.css, T101026002). */}
      <div className="home-dash-body">
        <div
          className="home-dash-side"
          style={{
            borderRight: "1px solid rgba(255,255,255,.09)",
            padding: "18px 14px",
          }}
        >
          {/* The real mark, not the ✦ glyph that stood in for it. The render is
              dark in both themes, so it always takes the dark-ground master —
              `logoFor` is asked about the ground, not about the page. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 700,
              fontSize: 13.5,
              padding: "4px 10px 16px",
            }}
          >
            <img
              src={logoFor(true)}
              alt=""
              aria-hidden="true"
              width={18}
              height={18}
              style={{ display: "block", flex: "none" }}
            />
            AI2Aim
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 10px",
              borderRadius: 8,
              background: "rgba(56,229,255,.12)",
              color: "#38E5FF",
              fontSize: 12.5,
              fontWeight: 600,
            }}
          >
            <span
              style={{
                width: 3,
                height: 14,
                borderRadius: 2,
                background: "#38E5FF",
              }}
            />
            Dashboard
          </div>
          {PLATFORM_SIDEBAR.map((item) => (
            <div key={item} style={sidebarItem}>
              {item}
            </div>
          ))}
        </div>

        <div className="home-dash-main">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                flex: 1,
                maxWidth: 300,
                background: "#1B1F25",
                border: "1px solid rgba(255,255,255,.08)",
                borderRadius: 999,
                padding: "8px 16px",
                fontSize: 12,
                color: "rgba(242,244,246,.55)",
              }}
            >
              Search anything...
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  background: "rgba(56,229,255,.16)",
                  color: "#38E5FF",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                AJ
              </span>
              <span style={{ lineHeight: 1.25 }}>
                <span
                  style={{ display: "block", fontSize: 12, fontWeight: 600 }}
                >
                  Alex Johnson
                </span>
                <span
                  style={{
                    display: "block",
                    fontSize: 10.5,
                    color: "rgba(242,244,246,.55)",
                  }}
                >
                  CEO
                </span>
              </span>
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>
              Welcome back, Alex
            </div>
            <div style={{ fontSize: 12, color: "rgba(242,244,246,.55)" }}>
              Here&rsquo;s what&rsquo;s happening with your business today.
            </div>
          </div>

          <div className="home-dash-stats">
            {PLATFORM_STATS.map((s) => (
              <div key={s.label} style={card}>
                <div
                  style={{ fontSize: 11, color: "rgba(242,244,246,.55)" }}
                >
                  {s.label}
                </div>
                <div
                  style={{ fontSize: 19, fontWeight: 700, margin: "3px 0" }}
                >
                  {s.value}
                </div>
                <div style={{ fontSize: 10.5 }}>
                  <span style={{ color: "#3DDC97", fontWeight: 700 }}>
                    {s.delta}
                  </span>{" "}
                  <span style={{ color: "rgba(242,244,246,.55)" }}>
                    vs last month
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="home-dash-lower">
            <div
              style={{ display: "flex", flexDirection: "column", gap: 10 }}
            >
              <div data-chart="" style={panel}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 12,
                  }}
                >
                  <span style={{ fontSize: 12.5, fontWeight: 700 }}>
                    Revenue Overview
                  </span>
                  <span
                    style={{
                      fontSize: 10.5,
                      color: "#38E5FF",
                      border: "1px solid rgba(56,229,255,.35)",
                      borderRadius: 999,
                      padding: "3px 10px",
                    }}
                  >
                    This Month
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: 14,
                    height: 108,
                    padding: "0 4px",
                  }}
                >
                  {PLATFORM_BARS.map((h, i) => (
                    <div
                      key={i}
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                        gap: 6,
                        height: "100%",
                      }}
                    >
                      <span
                        data-bar=""
                        style={{
                          display: "block",
                          height: h,
                          borderRadius: "6px 6px 3px 3px",
                          background:
                            "linear-gradient(180deg, #38E5FF, #0E6E80)",
                        }}
                      />
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 14, padding: "8px 4px 0" }}>
                  {PLATFORM_WEEKS.map((w) => (
                    <span key={w} style={weekLabel}>
                      {w}
                    </span>
                  ))}
                </div>
              </div>

              <div style={panel}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 10,
                  }}
                >
                  <span style={{ fontSize: 12.5, fontWeight: 700 }}>Tasks</span>
                  <span style={{ fontSize: 10.5, color: "#38E5FF" }}>
                    See all
                  </span>
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {PLATFORM_TASKS.map((t) => (
                    <div
                      key={t.title}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <span style={{ lineHeight: 1.3 }}>
                        <span
                          style={{
                            display: "block",
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          {t.title}
                        </span>
                        <span
                          style={{
                            display: "block",
                            fontSize: 10.5,
                            color: "rgba(242,244,246,.55)",
                          }}
                        >
                          {t.team}
                        </span>
                      </span>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: t.fg,
                          background: t.bg,
                          borderRadius: 999,
                          padding: "3px 9px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div
              style={{
                background: "#1B1F25",
                border: "1px solid rgba(56,229,255,.22)",
                borderRadius: 12,
                padding: "14px 16px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 7,
                    fontSize: 12.5,
                    fontWeight: 700,
                  }}
                >
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: "#38E5FF",
                      animation: "dcv2-pulseDot 2s ease-in-out infinite",
                    }}
                  />
                  AI Assistant
                </span>
                <span style={{ fontSize: 10.5, color: "#3DDC97" }}>
                  &#9679; Online
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  flex: 1,
                }}
              >
                <div
                  style={{
                    alignSelf: "flex-end",
                    maxWidth: "90%",
                    background: "rgba(56,229,255,.14)",
                    color: "#C9F5FD",
                    borderRadius: "10px 10px 3px 10px",
                    padding: "8px 11px",
                    fontSize: 11.5,
                  }}
                >
                  Why did new leads jump this month?
                </div>
                {ASSISTANT_REPLIES.map((r) => (
                  <div key={r} style={reply}>
                    {r}
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "10px 0" }}>
                {ASSISTANT_CHIPS.map((c) => (
                  <span
                    key={c}
                    style={{
                      fontSize: 10.5,
                      color: "#38E5FF",
                      border: "1px solid rgba(56,229,255,.35)",
                      borderRadius: 999,
                      padding: "4px 10px",
                    }}
                  >
                    {c}
                  </span>
                ))}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: "#14171C",
                  border: "1px solid rgba(255,255,255,.09)",
                  borderRadius: 999,
                  padding: "7px 8px 7px 14px",
                }}
              >
                <span
                  style={{
                    flex: 1,
                    fontSize: 11,
                    color: "rgba(242,244,246,.55)",
                  }}
                >
                  Ask anything about your data...
                </span>
                <span
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: "#38E5FF",
                    color: "#101216",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  &rarr;
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
